// AccountSettingsPage: Full-page account settings management
// Transform from modal to dedicated page for better mobile UX

import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/hooks/useAuth'
import {
  User,
  Mail,
  Camera,
  Bell,
  Globe,
  Save,
  AlertCircle,
  CheckCircle,
  Upload
} from 'lucide-react'

interface UserProfileData {
  full_name: string
  email: string
  preferred_export_platform: string
  avatar_url: string | null
  notification_preferences: {
    email_marketing: boolean
    email_updates: boolean
    email_security: boolean
  }
}

export function AccountSettingsPage() {
  const { user, signOut, updateProfile, uploadAvatar, loading } = useAuth()
  const [profileData, setProfileData] = useState<UserProfileData>({
    full_name: '',
    email: '',
    preferred_export_platform: 'shutterstock',
    avatar_url: null,
    notification_preferences: {
      email_marketing: false,
      email_updates: true,
      email_security: true
    }
  })

  const [isEditing, setIsEditing] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Initialize profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.user_metadata?.full_name || '',
        email: user.email || '',
        preferred_export_platform: user.user_metadata?.preferred_export_platform || 'shutterstock',
        avatar_url: user.user_metadata?.avatar_url || null,
        notification_preferences: {
          email_marketing: user.user_metadata?.email_marketing ?? false,
          email_updates: user.user_metadata?.email_updates ?? true,
          email_security: user.user_metadata?.email_security ?? true
        }
      })
    }
  }, [user])

  // Auto-dismiss messages
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000)
      return () => clearTimeout(timer)
    }
  }, [message])

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setUploadingAvatar(true)
    setMessage(null)

    try {
      const { data: avatarUrl, error } = await uploadAvatar(file)

      if (error) {
        throw error
      }

      if (avatarUrl) {
        // Update local state immediately for better UX
        setProfileData(prev => ({ ...prev, avatar_url: avatarUrl }))

        // Update profile in Supabase
        const { error: updateError } = await updateProfile({ avatar_url: avatarUrl })

        if (updateError) {
          throw new Error(updateError.message)
        }

        setMessage({ type: 'success', text: 'Profile photo updated successfully!' })
      }
    } catch (error) {
      console.error('Avatar upload error:', error)
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to upload profile photo'
      })
    } finally {
      setUploadingAvatar(false)
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleAvatarButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaveLoading(true)
    setMessage(null)

    try {
      const { error } = await updateProfile({
        full_name: profileData.full_name,
        preferred_export_platform: profileData.preferred_export_platform,
        email_marketing: profileData.notification_preferences.email_marketing,
        email_updates: profileData.notification_preferences.email_updates,
        email_security: profileData.notification_preferences.email_security
      })

      if (error) {
        throw new Error(error.message)
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' })
      setIsEditing(false)
    } catch (error) {
      console.error('Profile update error:', error)
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to update profile'
      })
    } finally {
      setSaveLoading(false)
    }
  }

  const handleCancel = () => {
    if (user) {
      setProfileData({
        full_name: user.user_metadata?.full_name || '',
        email: user.email || '',
        preferred_export_platform: user.user_metadata?.preferred_export_platform || 'shutterstock',
        avatar_url: user.user_metadata?.avatar_url || null,
        notification_preferences: {
          email_marketing: user.user_metadata?.email_marketing ?? false,
          email_updates: user.user_metadata?.email_updates ?? true,
          email_security: user.user_metadata?.email_security ?? true
        }
      })
    }
    setIsEditing(false)
    setMessage(null)
  }

  const exportPlatforms = [
    { id: 'shutterstock', name: 'Shutterstock' },
    { id: 'adobe-stock', name: 'Adobe Stock' },
    { id: 'istock', name: 'iStock by Getty Images' },
    { id: 'getty-images', name: 'Getty Images' },
    { id: 'generic', name: 'Generic Format' }
  ]

  const getUserInitials = () => {
    if (!profileData.full_name && !profileData.email) return 'U'
    const name = profileData.full_name || profileData.email
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  return (
    <div className="space-y-8">
      {/* Action Buttons */}
      <div className="flex items-center justify-end mb-6">
        <div className="flex items-center space-x-2">
            {!isEditing ? (
              <>
                {/* Mobile: Icon only button */}
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="md:hidden flex items-center justify-center bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                  aria-label="Edit profile"
                >
                  <User className="w-5 h-5" />
                </button>
                {/* Desktop: Full button with text */}
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="hidden md:flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
                >
                  <User className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
              </>
            ) : (
              <>
                {/* Mobile: Compact buttons */}
                <button
                  type="button"
                  onClick={handleCancel}
                  className="md:hidden px-3 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="account-settings-form"
                  disabled={saveLoading || loading}
                  className="md:hidden flex items-center space-x-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm text-sm font-medium"
                >
                  <Save className="w-4 h-4" />
                  <span>{saveLoading ? 'Saving...' : 'Save'}</span>
                </button>
                {/* Desktop: Full buttons */}
                <button
                  type="button"
                  onClick={handleCancel}
                  className="hidden md:block px-6 py-3 text-gray-600 hover:text-gray-800 font-medium rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="account-settings-form"
                  disabled={saveLoading || loading}
                  className="hidden md:flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm font-medium"
                >
                  <Save className="w-4 h-4" />
                  <span>{saveLoading ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </>
            )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {message && (
        <div className={`p-4 rounded-xl border flex items-center ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          )}
          <span className="font-medium">{message.text}</span>
          <button
            onClick={() => setMessage(null)}
            className="ml-auto text-xl leading-none hover:opacity-70"
          >
            ×
          </button>
        </div>
      )}

      <form id="account-settings-form" onSubmit={handleSave} className="space-y-8">
        {/* Profile Picture Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6">
            <div className="flex items-center space-x-6">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg">
                {profileData.avatar_url ? (
                  <img
                    src={profileData.avatar_url}
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  getUserInitials()
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Profile Picture</h3>
                <p className="text-gray-600 mb-4">Update your profile photo</p>
                <button
                  type="button"
                  onClick={handleAvatarButtonClick}
                  className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={uploadingAvatar}
                >
                  <Upload className="w-4 h-4" />
                  <span className="font-medium">
                    {uploadingAvatar ? 'Uploading...' : 'Upload New Photo'}
                  </span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              <User className="w-6 h-6 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="full_name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  id="full_name"
                  type="text"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-colors"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    value={profileData.email}
                    disabled
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                  />
                  <Mail className="absolute right-3 top-3.5 w-5 h-5 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-2">Email address cannot be changed here</p>
              </div>
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Globe className="w-6 h-6 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Export Preferences</h3>
            </div>

            <div>
              <label htmlFor="preferred_platform" className="block text-sm font-semibold text-gray-700 mb-2">
                Default Export Platform
              </label>
              <select
                id="preferred_platform"
                value={profileData.preferred_export_platform}
                onChange={(e) => setProfileData(prev => ({ ...prev, preferred_export_platform: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-colors"
              >
                {exportPlatforms.map(platform => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                This platform will be pre-selected when you export metadata
              </p>
            </div>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Bell className="w-6 h-6 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Notification Preferences</h3>
            </div>

            <div className="space-y-4">
              <label className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={profileData.notification_preferences.email_updates}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    notification_preferences: {
                      ...prev.notification_preferences,
                      email_updates: e.target.checked
                    }
                  }))}
                  disabled={!isEditing}
                  className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 mt-0.5"
                />
                <div>
                  <span className="font-medium text-gray-900">Product Updates</span>
                  <p className="text-sm text-gray-600">Get notified about new features and improvements</p>
                </div>
              </label>

              <label className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={profileData.notification_preferences.email_security}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    notification_preferences: {
                      ...prev.notification_preferences,
                      email_security: e.target.checked
                    }
                  }))}
                  disabled={!isEditing}
                  className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 mt-0.5"
                />
                <div>
                  <span className="font-medium text-gray-900">Security Alerts</span>
                  <p className="text-sm text-gray-600">Important security notifications and account changes</p>
                </div>
              </label>

              <label className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={profileData.notification_preferences.email_marketing}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    notification_preferences: {
                      ...prev.notification_preferences,
                      email_marketing: e.target.checked
                    }
                  }))}
                  disabled={!isEditing}
                  className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 mt-0.5"
                />
                <div>
                  <span className="font-medium text-gray-900">Marketing Communications</span>
                  <p className="text-sm text-gray-600">Tips, promotions, and special offers</p>
                </div>
              </label>
            </div>
          </div>
        </div>

      </form>
    </div>
  )
}

export default AccountSettingsPage